import React, { useEffect } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useExperienceBuilderContext } from "components/ExperienceBuilderEG/context/ExperienceBuilderContext";
import { Button } from "shared";

const containerStyle = {
  width: "100%",
  height: "585px",
};

const center = {
  lat: 54.2023,
  lng: -4.47735,
};

function MyComponent() {
  const { selectedCourses, selectedTours, selectedHotels, labels } =
    useExperienceBuilderContext();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: labels.GoogleApiKey,
  });

  const [map, setMap] = React.useState(null);

  const onLoad = React.useCallback(function callback(map: any) {
    const bounds = new window.google.maps.LatLngBounds(center);

    (map as any).fitBounds(bounds);
    (map as any).setZoom(5);

    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback(map: any) {
    setMap(null);
  }, []);

  const uniqueAirports = selectedCourses
    .flatMap((course) => course.Airports)
    .filter(
      (value, index, self) => index === self.findIndex((a) => a.Id === value.Id)
    );

  const fitAllMarkers = () => {
    let bounds = new google.maps.LatLngBounds();

    selectedCourses.forEach((course) => {
      bounds.extend({
        lat: Number(course.Location.Latitude),
        lng: Number(course.Location.Longitude),
      });
    });

    uniqueAirports.forEach((airport) => {
      bounds.extend({
        lat: Number(airport.Location.Latitude),
        lng: Number(airport.Location.Longitude),
      });
    });

    selectedHotels.forEach((hotel) => {
      bounds.extend({
        lat: Number(hotel.Location.Latitude),
        lng: Number(hotel.Location.Longitude),
      });
    });

    selectedTours.forEach((tour) => {
      bounds.extend({
        lat: Number(tour.Location.Latitude),
        lng: Number(tour.Location.Longitude),
      });
    });

    map && (map as any).fitBounds(bounds);
    if ((map as any).getZoom() >= 15) {
      (map as any).setZoom(15);
    }
  };

  useEffect(() => {
    if (!map) {
      return;
    }
    if (
      selectedCourses.length ||
      selectedHotels.length ||
      selectedTours.length
    ) {
      fitAllMarkers();
    } else {
      const bounds = new window.google.maps.LatLngBounds(center);

      (map as any).fitBounds(bounds);
      setTimeout(() => {
        (map as any).setZoom(5);
      }, 300);
    }
    // eslint-disable-next-line
  }, [selectedCourses, selectedHotels, selectedTours, map]);

  return (
    <div className="m-tailor-map__wrapper">
      <div className="m-tailor-map">
        <div className="m-tailor-map__title">
          <span>{labels.Common.LocationMap}</span>
          <Button
            type="link"
            text={labels.Common.BackToTop}
            onClick={() => {
              const componentContainer = document.getElementsByClassName(
                "o-tailored-experience"
              )?.[0];

              if (componentContainer) {
                componentContainer.scrollIntoView();
              }
            }}
            endIcon={<i className="zmdi zmdi-long-arrow-up" />}
          />
        </div>
        <span>{labels.Common.MapDescription}</span>
      </div>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {selectedCourses.map((course) => (
            <Marker
              position={{
                lat: Number(course.Location.Latitude),
                lng: Number(course.Location.Longitude),
              }}
              title={course.Title}
              icon={{
                url: "data:image/svg+xml;base64,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",
              }}
            />
          ))}

          {uniqueAirports.map((airport) => (
            <Marker
              position={{
                lat: Number(airport.Location.Latitude),
                lng: Number(airport.Location.Longitude),
              }}
              title={airport.Title}
              icon={{
                url: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAiIGhlaWdodD0iNjYiIHZpZXdCb3g9IjAgMCA3MCA2NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgZmlsdGVyPSJ1cmwoI2ZpbHRlcjBfZF8xMTkzXzc4MTUpIj4KPHJlY3QgeD0iMjAiIHk9IjE2IiB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHJ4PSIxNSIgZmlsbD0iIzI5OEZDMiIgc2hhcGUtcmVuZGVyaW5nPSJjcmlzcEVkZ2VzIi8+CjxwYXRoIGQ9Ik00NCAzNC4yNDQ0TDM2LjQ0NDQgMzEuODg4OVYzNy4wODg5TDM4LjMxMTEgMzguNTExMVYzOS45MzMzTDM1LjAyMjIgMzlMMzEuNjg4OSAzOS45MzMzVjM4LjUxMTFMMzMuNiAzNy4wODg5VjMxLjg4ODlMMjYgMzQuMjQ0NFYzMi4zNzc4TDMzLjYgMjcuNjIyMlYyMi40MjIyQzMzLjYgMjIuMDA3NCAzMy43MzMzIDIxLjY2NjcgMzQgMjEuNEMzNC4yNjY3IDIxLjEzMzMgMzQuNTkyNiAyMSAzNC45Nzc4IDIxQzM1LjM5MjYgMjEgMzUuNzMzMyAyMS4xMzMzIDM2IDIxLjRDMzYuMjk2MyAyMS42NjY3IDM2LjQ0NDQgMjIuMDA3NCAzNi40NDQ0IDIyLjQyMjJWMjcuNjIyMkw0NCAzMi4zNzc4VjM0LjI0NDRaIiBmaWxsPSJ3aGl0ZSIvPgo8L2c+CjxkZWZzPgo8ZmlsdGVyIGlkPSJmaWx0ZXIwX2RfMTE5M183ODE1IiB4PSIwIiB5PSIwIiB3aWR0aD0iNzAiIGhlaWdodD0iNzAiIGZpbHRlclVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgY29sb3ItaW50ZXJwb2xhdGlvbi1maWx0ZXJzPSJzUkdCIj4KPGZlRmxvb2QgZmxvb2Qtb3BhY2l0eT0iMCIgcmVzdWx0PSJCYWNrZ3JvdW5kSW1hZ2VGaXgiLz4KPGZlQ29sb3JNYXRyaXggaW49IlNvdXJjZUFscGhhIiB0eXBlPSJtYXRyaXgiIHZhbHVlcz0iMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMTI3IDAiIHJlc3VsdD0iaGFyZEFscGhhIi8+CjxmZU9mZnNldCBkeT0iNCIvPgo8ZmVHYXVzc2lhbkJsdXIgc3RkRGV2aWF0aW9uPSIxMCIvPgo8ZmVDb21wb3NpdGUgaW4yPSJoYXJkQWxwaGEiIG9wZXJhdG9yPSJvdXQiLz4KPGZlQ29sb3JNYXRyaXggdHlwZT0ibWF0cml4IiB2YWx1ZXM9IjAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAuMSAwIi8+CjxmZUJsZW5kIG1vZGU9Im5vcm1hbCIgaW4yPSJCYWNrZ3JvdW5kSW1hZ2VGaXgiIHJlc3VsdD0iZWZmZWN0MV9kcm9wU2hhZG93XzExOTNfNzgxNSIvPgo8ZmVCbGVuZCBtb2RlPSJub3JtYWwiIGluPSJTb3VyY2VHcmFwaGljIiBpbjI9ImVmZmVjdDFfZHJvcFNoYWRvd18xMTkzXzc4MTUiIHJlc3VsdD0ic2hhcGUiLz4KPC9maWx0ZXI+CjwvZGVmcz4KPC9zdmc+Cg==",
              }}
            />
          ))}

          {selectedHotels.map((hotel) => (
            <Marker
              position={{
                lat: Number(hotel.Location.Latitude),
                lng: Number(hotel.Location.Longitude),
              }}
              title={hotel.Title}
              icon={{
                url: "data:image/svg+xml;base64,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",
              }}
            />
          ))}
          {selectedTours.map((tour) => (
            <Marker
              position={{
                lat: Number(tour.Location.Latitude),
                lng: Number(tour.Location.Longitude),
              }}
              title={tour.Title}
              icon={{
                url: "data:image/svg+xml;base64,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",
              }}
            />
          ))}
        </GoogleMap>
      ) : (
        <></>
      )}
    </div>
  );
}

export default React.memo(MyComponent);
